import { enhanceMapClickData } from "../../utilities/dataSourceAdapters";
import { useSelectionActions, useSelectionSelector } from "../../store/selection";
import { usePopupActions, usePopupSelector } from "../../store/popupStore";
import { getCoordinatesCentroid } from "../../utilities/geoUtils";
import { SPATIAL_ACTIVE_LAYER_ID } from "../../constants/spatialDataset";
import { useMapStore } from "../../store/map";
import { stripCmSubLayerSuffix } from "../useCmTableMapLayers";

/**
 * Handles map click interactions and converts raw map features
 * into fully-hydrated geo entities.
 *
 * Responsibilities:
 * - Resolve correct API endpoint for clicked layer
 * - Normalize feature data using adapter system
 * - Update selectionStore (source of truth for WHAT is selected)
 * - Open the geo popup via popupStore with click coords (instantly known)
 */
export const useMapClickHandler = ({ config, findEndpointByLayerId }: any) => {
  const { selectedEntityId, selectedEndpoint } = useSelectionSelector();
  const { isOpen: isPopupOpen } = usePopupSelector();
  const { setSelectedEntityId, setSelectedEndpoint, setSelectedPrimaryStates, clearPrimarySelection } = useSelectionActions();
  const { openGeoPopup, openNonGeoPopup, closePopup } = usePopupActions();
  const activeSpatialDatasetEntityId = useMapStore((s) => s.activeSpatialDatasetEntityId);

  return async (e: any) => {
    if (!e.features || e.features.length === 0) return;

    const feature = e.features[0];
    // Normalise mixed-geometry CM-table sibling ids (…::line / …::fill) to the
    // canonical cm-table-<slug> id so endpoint resolution, adapters, and
    // selection highlighting all key off a single stable layer id.
    const layerId = stripCmSubLayerSuffix(feature.layer.id);

    /**
     * PMTiles vector spatial dataset features — these are geographic shapes within
     * the active dataset upload, not individual API entities. Clicking them should
     * (re-)open the corner detail card for the currently selected spatial dataset
     * rather than attempting a new API lookup, which would hit /media/unknown/ → 404.
     */
    if (layerId === SPATIAL_ACTIVE_LAYER_ID) {
      // Case 1: same endpoint, popup just closed — reopen it.
      if (selectedEntityId && !isPopupOpen) {
        openNonGeoPopup();
        return;
      }
      // Case 2: user navigated to a different table — restore the spatial dataset
      // selection (endpoint + entity) so the sidebar switches back and the corner
      // card re-opens. activeSpatialDatasetEntityId persists across endpoint switches.
      if (activeSpatialDatasetEntityId && activeSpatialDatasetEntityId !== selectedEntityId) {
        const spatialEndpoint = config?.apiEndpoints?.find((ep: any) => ep.entityType === "spatial-dataset");
        if (spatialEndpoint) {
          setSelectedPrimaryStates({
            selectedEndpoint: spatialEndpoint,
            selectedEntityId: activeSpatialDatasetEntityId,
            showRelationShip: false
          });
          openNonGeoPopup();
        }
      }
      return;
    }

    /**
     * Resolve endpoint early so entityType stays consistent
     * across map clicks, drawer selections, and deep links.
     */
    const endpoint = findEndpointByLayerId(layerId);

    let geoEntity;

    try {
      /**
       * Adapter system handles:
       * - joined data
       * - Fulcrum vs internal APIs
       * - ID normalization
       * - partial vs complete entity detection
       */
      geoEntity = await enhanceMapClickData(feature, layerId, endpoint || undefined, config);
    } catch (error) {
      console.error("[MapClick] Adapter failed, falling back:", error);

      /**
       * Fallback ensures map clicks never break selection,
       * even if adapters fail or data is incomplete.
       */
      geoEntity = {
        id: feature.properties?.id || feature.properties?.name || "unknown",
        name: feature.properties?.name || `${layerId} Feature`
      };
    }

    // Determine whether this click is on the same endpoint as before.
    // Captured BEFORE any store mutation so we have a stable baseline.
    const isSameEndpoint = !!endpoint && endpoint.tableName === selectedEndpoint?.tableName;

    // Reset primary selection only when switching to a different entity.
    if (geoEntity && geoEntity?.id !== selectedEntityId) {
      if (isSameEndpoint) {
        /**
         * Same endpoint — only wipe the entity + relationship flag.
         * Preserving selectedEndpoint in the store means the sidebar's
         * useInfiniteQuery `enabled` flag never toggles false→true, so
         * React Query never treats the query as newly-enabled and won't
         * re-fetch the listing API.
         */
        setSelectedPrimaryStates({ selectedEntityId: null, showRelationShip: false });
      } else {
        // Different endpoint — full reset is fine; the sidebar SHOULD re-query.
        clearPrimarySelection();
      }
      await new Promise((resolve) => setTimeout(resolve, 100)); // small delay to reset map ui's
    }

    /**
     * Update selectionStore FIRST — single source of truth for WHAT is selected.
     * React Query (useSelectedEntity) will fetch the full entity data reactively.
     */
    setSelectedEntityId(geoEntity.id);

    // Only call setSelectedEndpoint when the endpoint actually changed.
    // Calling it on every click (even same endpoint) would produce a new
    // object reference in the store → SidebarContent re-renders → React Query
    // sees staleTime:0 on re-enable and fires a new listing fetch.
    if (endpoint && !isSameEndpoint) {
      setSelectedEndpoint(endpoint);
    }

    /**
     * Open the correct popup type.
     * For map clicks we ALWAYS have coords from e.lngLat — pass them directly.
     * No need for a resolver hook; coords are available right now.
     */
    if (endpoint && geoEntity?.id) {
      let coords = [e.lngLat?.lng, e?.lngLat?.lat];
      const geomType = geoEntity?.geometry?.type?.toLowerCase();

      // Center the map popup to the entity location.
      // use entity longitude and latitude value to positioned the popup,
      // if entity geometry type is point or line calculate centroid position
      // to accurately show popup except polygon type.
      // if calculation of centroid failed fall back to entity coords.
      try {
        if (geomType === "point") {
          coords = geoEntity?.geometry?.coordinates;
        } else {
          // replace current coordinates with the centroid coordinates
          const { coordinates } = getCoordinatesCentroid(geoEntity?.geometry || []);
          coords = coordinates;
        }
      } catch (err) {
        console.error("[useMapClickHandler]: failed to calculate centroid of the coordinate, fallbacking to event coordinates. |||", err);
      }

      const isSameEntity = geoEntity.id === selectedEntityId;

      if (isSameEntity && isPopupOpen) {
        /**
         * Re-clicking the already-selected entity while the popup is open.
         * Close first so MapLibre fully unmounts the Popup, then reopen on
         * the next tick at the (possibly new) click coordinates.
         * Without this, calling openGeoPopup with identical state produces
         * no visible change and the popup appears frozen/missing.
         */
        closePopup();
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      openGeoPopup({ lng: coords?.[0], lat: coords?.[1] });
    } else {
      // Layers without a matching endpoint — no popup to show
      closePopup();
    }
  };
};
